import { data, type ActionFunctionArgs } from "react-router";
import { supabase } from "@shared/integrations/supabase/client";

export const errorLogsLoader = async () => {
	const { data, error } = await supabase
		.from("error_logs")
		.select("*")
		.order("created_at", { ascending: false });

	if (error) {
		throw error;
	}

	return { logs: data ?? [] };
};

export type ErrorLogsLoaderData = Awaited<ReturnType<typeof errorLogsLoader>>;

export const errorLogsAction = async ({ request }: ActionFunctionArgs) => {
	if (request.method !== "POST") {
		return data({ error: "Method not allowed" }, { status: 405 });
	}

	const formData = await request.formData();
	const intent = formData.get("intent");

	if (intent !== "resolve" && intent !== "resolve-all") {
		return data({ error: "Unsupported intent" }, { status: 400 });
	}

	if (intent === "resolve-all") {
		// First count unresolved logs, then update without returning rows to avoid PostgREST
		// bug when combining UPDATE + OR filters + SELECT.
		const { count, error: countError } = await supabase
			.from("error_logs")
			.select("id", { count: "exact", head: true })
			.or("resolved.is.null,resolved.eq.false");

		if (countError) {
			return data({ error: countError.message }, { status: 500 });
		}

		const { error } = await supabase
			.from("error_logs")
			.update({
				resolved: true,
				resolved_at: new Date().toISOString(),
				resolution_notes: "Marked as resolved via bulk action",
			})
			.or("resolved.is.null,resolved.eq.false");

		if (error) {
			return data({ error: error.message }, { status: 500 });
		}

		const resolvedCount = count ?? 0;
		return data({ ok: true, resolvedCount, logId: null });
	}

	const logId = formData.get("logId");
	const notes = formData.get("notes") ?? "";

	if (typeof logId !== "string") {
		return data({ error: "Missing logId", logId: null }, { status: 400 });
	}

	const { error } = await supabase
		.from("error_logs")
		.update({
			resolved: true,
			resolved_at: new Date().toISOString(),
			resolution_notes: typeof notes === "string" ? notes : "",
		})
		.eq("id", logId);

	if (error) {
		return data({ error: error.message, logId }, { status: 500 });
	}

	return data({ ok: true, logId });
};
